import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/utils";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

// Visual tone. `default` is the neutral dark note; `info` matches the solid info
// status badge (bg-info / info-foreground) for hover notes that read as a hint
// rather than a warning — same token as <Badge variant="info-solid" />.
const TOOLTIP_TONES = {
  default: "bg-foreground text-background",
  info: "bg-info text-info-foreground font-medium",
} as const;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    tone?: keyof typeof TOOLTIP_TONES;
  }
>(({ className, sideOffset = 4, tone = "default", ...props }, ref) => (
  // Portal to body so the tooltip escapes any sibling sticky stacking
  // context — the CRO Forecast FilterBar (z-10 sticky) and SubNav
  // (z-10 sticky) are siblings; without a Portal, the later-painted
  // SubNav visibly clipped tooltips anchored in the FilterBar above.
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md px-2.5 py-1.5 text-xs shadow-md animate-fade-in",
        TOOLTIP_TONES[tone],
        "data-[state=closed]:opacity-0",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
